
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

export const useProductStats = (productId: string | undefined) => {
  const { user } = useAuth();
  
  // Track view count
  useEffect(() => {
    const recordView = async () => {
      if (!productId) return;
      
      try {
        // Record a view in the product_views table
        // Use the generic version of from() to bypass TypeScript checking
        await supabase
          .from('product_views')
          .insert({
            product_id: parseInt(productId),
            viewer_id: user?.id || null,
            // Optional: Add IP and user agent tracking if needed
          } as any);
      } catch (error) {
        // Error handling without console.log
      }
    };
    
    recordView();
  }, [productId, user?.id]);
  
  // Fetch product statistics
  const { data: stats, isLoading } = useQuery({
    queryKey: ['productStats', productId],
    queryFn: async () => {
      if (!productId) return { viewCount: 0, favoriteCount: 0 };
      
      const parsedId = parseInt(productId);
      
      // Get view count - use the generic version to bypass type checking
      const { count: viewCount, error: viewError } = await supabase
        .from('product_views')
        .select('*', { count: 'exact', head: false } as any)
        .eq('product_id', parsedId);
        
      if (viewError) {
        // Error handling without console.log
      }
      
      // Get favorite count
      const { count: favoriteCount, error: favError } = await supabase
        .from('favorites')
        .select('*', { count: 'exact', head: false })
        .eq('listing_id', parsedId);
        
      if (favError) {
        // Error handling without console.log
      }
      
      return {
        viewCount: viewCount || 0,
        favoriteCount: favoriteCount || 0
      };
    },
    enabled: !!productId,
  });
  
  return {
    viewCount: stats?.viewCount || 0,
    favoriteCount: stats?.favoriteCount || 0,
    isLoading
  };
};
